import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { appleMusicService } from '../services/appleMusicService';
import { saveAuthToken, getAuthToken, deleteAuthToken } from '../lib/tokenStorage';
import { useAuth } from '../contexts/AuthContext';

// Define types for MusicKit global object
declare global {
  interface Window {
    MusicKit: any;
    AppleID: any;
  }
}

// Define the context type
interface AppleMusicContextType {
  isAuthenticated: boolean;
  instance: any; // MusicKit instance
  login: () => Promise<void>;
  logout: () => void;
  initialize: () => Promise<any>;
  isInitializing: boolean;
  userStorefront: string | null;
}

// Create context with a default value
const AppleMusicContext = createContext<AppleMusicContextType>({
  isAuthenticated: false,
  instance: null,
  login: async () => {
    console.warn('Apple Music context not initialized');
  },
  logout: () => {
    console.warn('Apple Music context not initialized');
  },
  initialize: async () => {
    console.warn('Apple Music context not initialized');
    return null;
  },
  isInitializing: false,
  userStorefront: null,
});

// Provider component
export function AppleMusicProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [instance, setInstance] = useState<any>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [userStorefront, setUserStorefront] = useState<string | null>(null);
  const [musicUserToken, setMusicUserToken] = useState<string | null>(null);
  const { user } = useAuth();

  // Helper function to determine if we should use local authorization
  const shouldUseLocalAuth = async () => {
    if (!user) return true; // No user, rely on local auth
    
    // Check if we have local authorization
    const localAuthorized = localStorage.getItem('apple_music_authorized') === 'true';
    
    if (!localAuthorized) return false; // No local auth, don't use
    
    try {
      // Check if we have Supabase token
      const supabaseToken = await getAuthToken('apple');
      
      if (!supabaseToken || !supabaseToken.access_token) {
        // No Supabase token but we have local auth, save to Supabase
        console.log('Found local Apple Music auth but no Supabase record, will save to Supabase when available');
        return true;
      }
      
      // Compare timestamps if possible
      const localStorageTimestamp = localStorage.getItem('apple_music_authorized_timestamp');
      const localTimestamp = localStorageTimestamp ? parseInt(localStorageTimestamp) : 0;
      
      // Get timestamp from Supabase as a string value
      const supabaseTimestamp = supabaseToken.updated_at ? supabaseToken.updated_at : '0';
      
      // Log comparison values for debugging
      console.log('Comparing timestamps - Local:', localTimestamp, 'Supabase:', supabaseTimestamp);
      
      // Convert supabase timestamp to number if it's a string number, 
      // or get timestamp from Date object if it's a date string
      let supabaseTime = 0;
      if (!isNaN(Number(supabaseTimestamp))) {
        supabaseTime = Number(supabaseTimestamp);
      } else {
        try {
          supabaseTime = new Date(supabaseTimestamp).getTime();
        } catch (e) {
          console.error('Error parsing Supabase timestamp:', e);
        }
      }
      
      if (localTimestamp > supabaseTime) {
        console.log('Local Apple Music auth is newer than Supabase, updating Supabase');
        return true;
      }
    } catch (error) {
      console.error('Error checking Supabase for Apple Music token:', error);
      return true; // On error, use local auth
    }
    
    return false;
  };

  // Initialize MusicKit
  const initialize = async () => {
    if (isInitializing) return instance;
    
    setIsInitializing(true);
    try {
      if (!window.MusicKit) {
        // Load MusicKit JS if not already loaded
        await appleMusicService.loadMusicKitJS();
      }

      if (window.MusicKit && !isInitialized) {
        const musicKitInstance = await appleMusicService.initializeMusicKit();
        setInstance(musicKitInstance);
        setIsInitialized(true);
        
        // Check if user is already authorized
        if (musicKitInstance.isAuthorized) {
          setIsAuthenticated(true);
          console.log('User is already authorized with Apple Music');
          // Get user storefront if already authorized
          await fetchUserStorefront(musicKitInstance);
          
          // If user is logged in, make sure token is in Supabase
          if (user) {
            // Check if we should update Supabase
            if (await shouldUseLocalAuth()) {
              console.log('Ensuring Apple Music token is saved to Supabase');
              try {
                // We don't have direct access to the music user token here,
                // but we'll mark the account as authorized in Supabase
                const currentTimestamp = Date.now().toString();
                await saveAuthToken('apple', {
                  access_token: 'apple-music-authorized', // Placeholder
                  user_identifier: user.email || user.id,
                  expires_at: currentTimestamp // Store timestamp as string
                });
              } catch (saveError) {
                console.error('Failed to save Apple Music token status to Supabase:', saveError);
              }
            }
          }
        } else {
          console.log('User is not authorized with Apple Music');
          
          // Try to restore authorization from Supabase if user is logged in
          if (user) {
            try {
              const savedToken = await getAuthToken('apple');
              if (savedToken && savedToken.access_token) {
                console.log('Found saved Apple Music token, attempting to restore session');
                try {
                  // For Apple Music, we need to use the token to restore the session
                  // This is a workaround as Apple Music doesn't support direct token restoration
                  // We'll trigger a new authorization which will be faster for the user
                  await login();
                } catch (restoreError) {
                  console.error('Failed to restore Apple Music session:', restoreError);
                }
              }
            } catch (error) {
              console.error('Error retrieving Apple Music token:', error);
            }
          }
        }

        // Set up event listeners for authorization state changes
        musicKitInstance.addEventListener('authorizationStatusDidChange', (event: any) => {
          console.log('Apple Music auth status changed:', event.authorizationStatus);
          const isAuth = musicKitInstance.isAuthorized;
          setIsAuthenticated(isAuth);
          
          // Try to fetch user storefront when authorization changes to true
          if (isAuth) {
            fetchUserStorefront(musicKitInstance);
            
            // Store authorization state in localStorage
            localStorage.setItem('apple_music_authorized', 'true');
            // Also save timestamp of authorization
            const currentTimestamp = Date.now().toString();
            localStorage.setItem('apple_music_authorized_timestamp', currentTimestamp);
            
            // If we have a music user token and the user is logged in to Supabase, save to Supabase
            if (musicUserToken && user) {
              saveAuthToken('apple', {
                access_token: musicUserToken,
                user_identifier: user.email || user.id,
                expires_at: currentTimestamp // Store timestamp as string
              }).then(() => {
                console.log('Apple Music token saved to Supabase');
              }).catch(error => {
                console.error('Failed to save Apple Music token:', error);
              });
            }
          } else {
            // Clear authorization state
            localStorage.removeItem('apple_music_authorized');
            localStorage.removeItem('apple_music_authorized_timestamp');
            setMusicUserToken(null);
            
            // If user is logged in to Supabase, remove the token
            if (user) {
              deleteAuthToken('apple').catch(error => {
                console.error('Failed to delete Apple Music token:', error);
              });
            }
          }
        });

        return musicKitInstance;
      }
      return instance;
    } catch (error) {
      console.error('Error initializing Apple Music:', error);
      return null;
    } finally {
      setIsInitializing(false);
    }
  };
  
  // Helper function to fetch the user's storefront
  const fetchUserStorefront = async (musicKitInstance: any) => {
    if (!musicKitInstance || !musicKitInstance.isAuthorized) return null;
    
    try {
      const response = await musicKitInstance.api.music('v1/me/storefront');
      if (response?.data?.data?.[0]?.id) {
        const storefrontId = response.data.data[0].id;
        console.log('User storefront:', storefrontId);
        setUserStorefront(storefrontId);
        
        // Save storefront to localStorage for persistence
        localStorage.setItem('apple_music_storefront', storefrontId);
        
        return storefrontId;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user storefront:', error);
      return null;
    }
  };

  // Try to initialize on mount
  useEffect(() => {
    initialize();
    
    return () => {
      // Clean up event listeners if needed
      if (instance) {
        try {
          instance.removeEventListener('authorizationStatusDidChange');
        } catch (error) {
          console.error('Error removing event listeners:', error);
        }
      }
    };
  }, []);
  
  // Try to restore storefront from localStorage
  useEffect(() => {
    const savedStorefront = localStorage.getItem('apple_music_storefront');
    if (savedStorefront) {
      setUserStorefront(savedStorefront);
    }
  }, []);

  const login = async () => {
    try {
      if (!instance && !isInitialized) {
        const newInstance = await initialize();
        if (!newInstance) {
          console.error('Failed to initialize Apple Music');
          return;
        }
      }
      
      if (instance) {
        console.log('Attempting to authorize with Apple Music...');
        try {
          const token = await instance.authorize();
          console.log('Apple Music authorization successful, music user token:', token ? 'Received' : 'Not received');
          
          // Save the music user token if we get one
          if (token) {
            setMusicUserToken(token);
            
            // If user is logged in to Supabase, save the token
            if (user) {
              await saveAuthToken('apple', {
                access_token: token,
                user_identifier: user.email || user.id
              });
              console.log('Apple Music token saved to Supabase');
            }
          }
          
          // Authorization status should be updated by the event listener
          // But we'll set it directly as well to be sure
          setIsAuthenticated(instance.isAuthorized);
          
          // Store the token in localStorage for persistence
          if (instance.isAuthorized) {
            localStorage.setItem('apple_music_authorized', 'true');
            
            // Fetch user storefront after successful authorization
            await fetchUserStorefront(instance);
          }
        } catch (authError) {
          console.error('Error during Apple Music authorization:', authError);
          // Clear any stale authorization state
          localStorage.removeItem('apple_music_authorized');
          setIsAuthenticated(false);
          setMusicUserToken(null);
          
          // If user is logged in to Supabase, remove the token
          if (user) {
            try {
              await deleteAuthToken('apple');
            } catch (error) {
              console.error('Failed to delete Apple Music token:', error);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error logging in to Apple Music:', error);
    }
  };

  const logout = async () => {
    try {
      if (instance) {
        instance.unauthorize();
        setIsAuthenticated(false);
        setUserStorefront(null);
        setMusicUserToken(null);
        localStorage.removeItem('apple_music_authorized');
        localStorage.removeItem('apple_music_storefront');
        console.log('Logged out from Apple Music');
        
        // If user is logged in to Supabase, remove the token
        if (user) {
          try {
            await deleteAuthToken('apple');
            console.log('Apple Music token deleted from Supabase');
          } catch (error) {
            console.error('Failed to delete Apple Music token:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error logging out from Apple Music:', error);
    }
  };

  const contextValue = {
    isAuthenticated,
    instance,
    login,
    logout,
    initialize,
    isInitializing,
    userStorefront
  };

  return (
    <AppleMusicContext.Provider value={contextValue}>
      {children}
    </AppleMusicContext.Provider>
  );
}

// Hook for using the context
export function useAppleMusic() {
  return useContext(AppleMusicContext);
} 